import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ArmeriaLayout from "./ArmeriaLayout";
import { LoginRequiredModal } from "./LoginRequiredModal";
import { getToolConfig } from "./armeriaConfig";
import { RENDERERS } from "./ArmeriaResults";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";
import { Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import "./armeria.css";

/** Dynamic tool page — reads config, builds form, gates result behind login. */
export default function ArmeriaToolPage() {
  const { toolId } = useParams();
  const navigate = useNavigate();
  const { user, register } = useAuth();
  const config = getToolConfig(toolId);

  const [inputs, setInputs] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [pendingInputs, setPendingInputs] = useState(null);
  const [autoRunTriggered, setAutoRunTriggered] = useState(false);
  // Unified signup fields (shown only when there's no session)
  const [signupData, setSignupData] = useState({ name: "", email: "", password: "" });
  const resultRef = useRef(null);

  // Initialize default values (for range sliders etc.)
  useEffect(() => {
    if (!config) return;
    const defaults = {};
    config.fields.forEach(f => {
      if (f.defaultValue !== undefined) defaults[f.name] = f.defaultValue;
    });
    setInputs(defaults);
  }, [toolId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-resubmit if the user came back from /registro after seeing the modal
  useEffect(() => {
    if (autoRunTriggered || !user || !config) return;
    try {
      // Read from localStorage (persistent) — fallback: sessionStorage (legacy)
      const raw = localStorage.getItem("armeria_pending_generate") || sessionStorage.getItem("armeria_pending_generate");
      if (raw) {
        const pending = JSON.parse(raw);
        if (pending && pending.tool_id === toolId && pending.inputs) {
          setInputs(pending.inputs);
          localStorage.removeItem("armeria_pending_generate");
          localStorage.removeItem("armeria_return_to");
          sessionStorage.removeItem("armeria_pending_generate");
          sessionStorage.removeItem("armeria_return_to");
          setAutoRunTriggered(true);
          setTimeout(() => doGenerate(pending.inputs), 100);
        }
      }
    } catch {/* silent */}
  }, [user, toolId, config]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!config) {
    return <ArmeriaLayout title="Herramienta no encontrada"><p>La herramienta <code>{toolId}</code> no existe. <a href="/armeria" style={{color:"#c8922a"}}>Volver</a>.</p></ArmeriaLayout>;
  }

  const setField = (name, value) => setInputs(prev => ({ ...prev, [name]: value }));

  const validate = (currInputs) => {
    for (const f of config.fields) {
      if (f.optional) continue;
      const v = currInputs[f.name];
      if (v === undefined || v === null || (typeof v === "string" && !v.trim())) {
        toast.error(`Rellena: ${f.label}`);
        return false;
      }
      if (f.minWords && typeof v === "string") {
        const w = v.trim().split(/\s+/).length;
        if (w < f.minWords) {
          toast.error(`${f.label}: mínimo ${f.minWords} palabras (llevas ${w})`);
          return false;
        }
      }
    }
    return true;
  };

  const doGenerate = async (rawInputs) => {
    const payload = config.transformInputs ? config.transformInputs(rawInputs) : rawInputs;
    setLoading(true);
    setResult(null);
    try {
      // Kick off the job (returns instantly, avoids Cloudflare 100s idle timeout)
      const kick = await api.post("/armeria/generate-job", { tool_id: toolId, inputs: payload });
      const jobId = kick.data?.job_id;
      if (!jobId) throw new Error("No job_id returned");
      // Poll status every 3s, up to ~180s (60 attempts)
      const startedAt = Date.now();
      const maxWaitMs = 180000;
      const pollOnce = async () => {
        const st = await api.get(`/armeria/generate-status/${jobId}`);
        return st.data;
      };
      const wait = (ms) => new Promise(r => setTimeout(r, ms));
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await wait(3000);
        try {
          const status = await pollOnce();
          if (status.status === "completed") {
            setResult(status.result);
            setLoading(false);
            return;
          }
          if (status.status === "error") {
            toast.error(status.detail || "Error al generar");
            setLoading(false);
            return;
          }
        } catch (pollErr) {
          if (pollErr.response?.status === 404) {
            toast.error("Job expirado. Vuelve a intentarlo.");
            setLoading(false);
            return;
          }
          // Transient network hiccup — keep polling
        }
        if (Date.now() - startedAt > maxWaitMs) {
          toast.error("La generación tardó demasiado. Intenta de nuevo con menos texto.");
          setLoading(false);
          return;
        }
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setPendingInputs(rawInputs);
        setShowLoginModal(true);
      } else {
        toast.error(err.response?.data?.detail || "Error al iniciar. Intenta de nuevo.");
      }
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!validate(inputs)) return;
    if (!user) {
      // Unified signup path: if the inline fields are filled, register first
      // then generate. Otherwise, fall back to the modal (for users who prefer login).
      const { name, email, password } = signupData;
      if (name.trim() && email.trim() && password.trim()) {
        if (password.length < 8) {
          toast.error("La contraseña debe tener al menos 8 caracteres");
          return;
        }
        setLoading(true);
        try {
          await register(name.trim(), email.trim().toLowerCase(), password, `armeria-${toolId}`);
          toast.success("Cuenta creada. Analizando...");
          // Now user is set — call generate directly
          doGenerate(inputs);
          return;
        } catch (err) {
          setLoading(false);
          toast.error(err.response?.data?.detail || "No se pudo crear la cuenta. Intenta con otro email.");
          return;
        }
      }
      // No inline signup data → show modal (login path or explicit signup)
      setPendingInputs(inputs);
      setShowLoginModal(true);
      return;
    }
    doGenerate(inputs);
  };

  const handleDownloadPDF = async () => {
    if (!resultRef.current) return;
    try {
      const html2pdf = (await import("html2pdf.js")).default;
      const opt = {
        margin: [10, 10, 10, 10],
        filename: `${config.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.pdf`,
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: "#0c0a07" },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      };
      toast.info("Generando PDF...");
      await html2pdf().set(opt).from(resultRef.current).save();
    } catch (e) {
      toast.error("Error al generar el PDF");
    }
  };

  const Renderer = RENDERERS[config.resultRenderer];

  return (
    <ArmeriaLayout title={`${config.emoji} ${config.title}`} subtitle={config.tagline}>
      {result && Renderer ? (
        <div>
          <div ref={resultRef} style={{ background: "#0c0a07", padding: "16px" }}>
            <Renderer data={result} />
          </div>
          <div style={{ textAlign: "center", marginTop: "24px", display: "flex", gap: "10px", justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={handleDownloadPDF} className="arm-btn-outline" data-testid="armeria-download-pdf" style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
              <Download size={14} /> Descargar PDF
            </button>
            <button onClick={() => { setResult(null); }} className="arm-btn-outline" data-testid="armeria-tool-retry">Hacer otro</button>
            <button onClick={() => navigate("/armeria")} className="arm-btn-primary" data-testid="armeria-tool-more">Explorar más herramientas</button>
          </div>
        </div>
      ) : (
        <div className="arm-card">
          {config.fields.map(field => (
            <div key={field.name} style={{ marginBottom: "18px" }}>
              <label className="arm-label">
                {field.label}
                {field.optional && <span style={{ color: "#a89b83", fontWeight: 400, marginLeft: "6px", fontSize: "12px" }}>(opcional)</span>}
              </label>
              {field.type === "textarea" && (
                <textarea className="arm-textarea" placeholder={field.placeholder}
                  value={inputs[field.name] || ""} onChange={(e) => setField(field.name, e.target.value)}
                  data-testid={`armeria-input-${field.name}`} />
              )}
              {field.type === "text" && (
                <input type="text" className="arm-input" placeholder={field.placeholder}
                  value={inputs[field.name] || ""} onChange={(e) => setField(field.name, e.target.value)}
                  data-testid={`armeria-input-${field.name}`} />
              )}
              {field.type === "select" && (
                <select className="arm-select" value={inputs[field.name] || ""}
                  onChange={(e) => setField(field.name, e.target.value)}
                  data-testid={`armeria-input-${field.name}`}>
                  <option value="">Selecciona una opción...</option>
                  {field.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              )}
              {field.type === "options" && (
                <div>
                  {field.options.map(o => (
                    <button key={o.value} type="button"
                      className={`arm-option-btn ${inputs[field.name] === o.value ? "selected" : ""}`}
                      onClick={() => setField(field.name, o.value)}
                      data-testid={`armeria-option-${field.name}-${o.value}`}>
                      {o.label}
                    </button>
                  ))}
                </div>
              )}
              {field.type === "range" && (
                <div>
                  <input type="range" min={field.min} max={field.max} step={field.step}
                    value={inputs[field.name] || field.defaultValue}
                    onChange={(e) => setField(field.name, parseInt(e.target.value))}
                    style={{ width: "100%", accentColor: "#c8922a" }}
                    data-testid={`armeria-input-${field.name}`} />
                  <div style={{ textAlign: "right", color: "#c8922a", fontFamily: "'IBM Plex Mono', monospace", fontSize: "14px", marginTop: "4px" }}>
                    {inputs[field.name] || field.defaultValue}
                  </div>
                </div>
              )}
              {field.help && <div className="arm-hint">{field.help}</div>}
            </div>
          ))}

          {/* Inline signup fields — shown only when there is no user session.
              This is the "unified" flow: user fills tool + signup in a single step. */}
          {!user && (
            <div style={{
              marginTop: "24px", padding: "20px", border: "1px solid rgba(200,146,42,0.4)",
              borderRadius: "12px", background: "rgba(200,146,42,0.04)"
            }}>
              <div style={{ marginBottom: "12px" }}>
                <span className="arm-badge">Último paso — crea tu cuenta gratis</span>
              </div>
              <p style={{ color: "#a89b83", fontSize: "13px", marginBottom: "14px", lineHeight: "1.5" }}>
                Necesitamos tus datos para guardar tu análisis. <strong style={{ color: "#f5efe4" }}>Sin tarjeta. Herramientas 100% gratuitas.</strong>
              </p>
              <div style={{ marginBottom: "12px" }}>
                <label className="arm-label">Nombre</label>
                <input type="text" className="arm-input" placeholder="Tu nombre"
                  value={signupData.name}
                  onChange={(e) => setSignupData({ ...signupData, name: e.target.value })}
                  data-testid="armeria-signup-name" />
              </div>
              <div style={{ marginBottom: "12px" }}>
                <label className="arm-label">Email</label>
                <input type="email" className="arm-input" placeholder="tu@email.com"
                  value={signupData.email}
                  onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                  data-testid="armeria-signup-email" />
              </div>
              <div>
                <label className="arm-label">Contraseña</label>
                <input type="password" className="arm-input" placeholder="Mínimo 8 caracteres"
                  value={signupData.password}
                  onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                  data-testid="armeria-signup-password" />
              </div>
              <p style={{ marginTop: "12px", textAlign: "center", fontSize: "12px", color: "#a89b83" }}>
                ¿Ya tienes cuenta? <button type="button" onClick={() => { setPendingInputs(inputs); setShowLoginModal(true); }} style={{ background: "transparent", border: "none", color: "#c8922a", cursor: "pointer", textDecoration: "underline", fontFamily: "inherit", fontSize: "12px" }} data-testid="armeria-login-link">Inicia sesión</button>
              </p>
            </div>
          )}

          <button onClick={handleGenerate} disabled={loading}
            className="arm-btn-primary" style={{ width: "100%", marginTop: "20px" }}
            data-testid="armeria-generate-btn">
            {loading ? (<><Loader2 size={16} style={{ display: "inline", marginRight: "8px", animation: "spin 1s linear infinite" }} /> Analizando con IA...</>) : (user ? config.ctaLabel : `Crear cuenta y ${config.ctaLabel.toLowerCase()}`)}
          </button>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      <LoginRequiredModal
        open={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        toolId={toolId}
        inputs={pendingInputs || inputs}
      />
    </ArmeriaLayout>
  );
}
